import React, { useState, useEffect } from 'react';
import { X, Loader2, ArrowDownLeft, ArrowUpRight, FileText, ExternalLink } from 'lucide-react';
import { productAPI } from '../../services/api';

const typeColors = {
  'Inward': 'bg-green-50 text-green-700 border-green-200',
  'Stock Entry': 'bg-blue-50 text-blue-700 border-blue-200',
  'Stock Transfer': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Dispatch': 'bg-red-50 text-red-700 border-red-200',
  'Production': 'bg-amber-50 text-amber-700 border-amber-200',
  'Production (Cancelled)': 'bg-red-50 text-red-700 border-red-200',
  'Production (FG)': 'bg-purple-50 text-purple-700 border-purple-200',
  'Sales Return': 'bg-teal-50 text-teal-700 border-teal-200',
  'Manual Adjustment': 'bg-orange-50 text-orange-700 border-orange-200',
};

// Maps document_type to the frontend route — ALL open in new tab
const getDocumentLink = (movement) => {
  const type = movement.document_type;
  const id = movement.document_id;
  if (!type || !id) return null;
  switch (type) {
    case 'inward': return { url: `/dashboard/inwards?view=${id}`, newTab: true };
    case 'dispatch': {
      if (movement.oc_id) {
        return { url: `/dashboard/sales?tab=order-confirmations&oc=${movement.oc_id}`, newTab: true };
      }
      return { url: `/dashboard/dispatches?view=${id}`, newTab: true };
    }
    case 'production': return { url: `/dashboard/production?view=${id}`, newTab: true };
    case 'stock_transfer': return { url: `/dashboard/sales?tab=stock-transfer&stn=${id}`, newTab: true };
    case 'sales_return': return { url: `/dashboard/sales?tab=sales-returns&sr=${id}`, newTab: true };
    default: return null;
  }
};

const ItemHistoryDialog = ({ productId, productName, branchId, branchName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const result = await productAPI.getHistory(productId, branchId);
        setData(result);
      } catch (err) {
        console.error('Failed to load history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [productId, branchId]);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="item-history-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Item History{branchName ? ` — ${branchName}` : ''}</h2>
            <p className="text-[11px] text-gray-500">{productName} {data?.product_sku ? `(${data.product_sku})` : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : !data ? (
          <div className="text-center py-16"><p className="text-sm text-gray-500">Failed to load history</p></div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100">
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">Total In</p>
                <p className="text-sm font-bold text-green-600">{data.total_in?.toLocaleString()} {data.unit}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">Total Out</p>
                <p className="text-sm font-bold text-red-600">{data.total_out?.toLocaleString()} {data.unit}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">Current Balance</p>
                <p className="text-sm font-bold" style={{ color: '#24549c' }}>{data.current_balance?.toLocaleString()} {data.unit}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">Movements</p>
                <p className="text-sm font-bold text-gray-700">{data.movements?.length || 0}</p>
              </div>
            </div>

            {/* Movement Table */}
            <div className="flex-1 overflow-auto px-5 py-3">
              {(!data.movements || data.movements.length === 0) ? (
                <div className="text-center py-12">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No movements recorded yet</p>
                </div>
              ) : (
                <table className="w-full text-xs" data-testid="history-table">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Type</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Document</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Party/Customer</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Branch</th>
                      <th className="text-right px-2 py-2 text-[10px] font-semibold text-green-600 uppercase">Qty In</th>
                      <th className="text-right px-2 py-2 text-[10px] font-semibold text-red-600 uppercase">Qty Out</th>
                      <th className="text-right px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Rate</th>
                      <th className="text-right px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.movements.map((m, idx) => {
                      const docLink = getDocumentLink(m);
                      const isReverted = !!m.reverted;
                      const isAuditOnly = !!m.audit_only;
                      const isMuted = isReverted || isAuditOnly;
                      const origAdj = m.original_adjustment;
                      const badgeLabel = isReverted ? 'REVERTED' : (isAuditOnly ? 'AUDIT' : null);
                      const badgeClass = isReverted
                        ? 'bg-red-100 text-red-700 border-red-200'
                        : 'bg-blue-100 text-blue-700 border-blue-200';
                      return (
                        <tr key={idx}
                          className={`hover:bg-gray-50/50 ${isMuted ? 'opacity-60 bg-gray-50' : ''}`}
                          data-testid={`history-row-${idx}`}>
                          <td className={`px-2 py-2 text-xs whitespace-nowrap ${isMuted ? 'text-gray-500 line-through' : 'text-gray-700'}`}>{m.date || '-'}</td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded-full border ${typeColors[m.type] || 'bg-gray-50 text-gray-600 border-gray-200'} ${isMuted ? 'line-through' : ''}`}>
                                {m.qty_in > 0 || (isMuted && origAdj > 0) ? <ArrowDownLeft className="w-2.5 h-2.5" /> : <ArrowUpRight className="w-2.5 h-2.5" />}
                                {m.type}
                              </span>
                              {badgeLabel && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold rounded border ${badgeClass}`} data-testid={`history-${badgeLabel.toLowerCase()}-${idx}`}>
                                  {badgeLabel}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            {docLink ? (
                                <a href={docLink.url} target="_blank" rel="noopener noreferrer" data-testid={`doc-link-${idx}`}
                                  className={`inline-flex items-center gap-1 text-[11px] font-medium hover:underline cursor-pointer ${isMuted ? 'line-through' : ''}`} style={{ color: '#24549c' }}>
                                  <FileText className="w-3 h-3" />
                                  {m.document_number || m.document_id?.substring(0, 12)}
                                  <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            ) : (
                              <span className={`text-[11px] text-gray-600 font-mono ${isMuted ? 'line-through' : ''}`}>{m.document_number || '-'}</span>
                            )}
                            {m.reference && <p className="text-[9px] text-gray-400">Ref: {m.reference}</p>}
                            {isReverted && m.revert_reason && (
                              <p className="text-[9px] text-red-600 italic mt-0.5">↺ {m.revert_reason}</p>
                            )}
                            {isAuditOnly && (
                              <p className="text-[9px] text-blue-700 italic mt-0.5">ℹ Opening row was updated directly — this audit entry has no balance impact.</p>
                            )}
                          </td>
                          <td className={`px-2 py-2 text-xs ${isMuted ? 'text-gray-500 line-through' : 'text-gray-700'}`}>{m.party || '-'}</td>
                          <td className={`px-2 py-2 text-xs ${isMuted ? 'text-gray-500' : 'text-gray-600'}`}>{m.branch || '-'}</td>
                          <td className="px-2 py-2 text-right">
                            {isMuted ? (
                              origAdj > 0 ? <span className="text-xs text-gray-400 line-through">+{origAdj.toLocaleString()}</span> : <span className="text-gray-300">-</span>
                            ) : (
                              m.qty_in > 0 ? <span className="text-xs font-semibold text-green-600">+{m.qty_in.toLocaleString()}</span> : <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {isMuted ? (
                              origAdj < 0 ? <span className="text-xs text-gray-400 line-through">-{Math.abs(origAdj).toLocaleString()}</span> : <span className="text-gray-300">-</span>
                            ) : (
                              m.qty_out > 0 ? <span className="text-xs font-semibold text-red-600">-{m.qty_out.toLocaleString()}</span> : <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className={`px-2 py-2 text-right text-xs ${isMuted ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{m.rate > 0 ? `₹${m.rate.toLocaleString()}` : '-'}</td>
                          <td className="px-2 py-2 text-right">
                            <span className={`text-xs font-bold ${m.balance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{m.balance?.toLocaleString()}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ItemHistoryDialog;
